/* tslint:disable:typedef */
import documentsFactory from '@Common/ApplicationCore/DocumentsFactory';
import { asyncWaiter, IAsyncWaiterSetting } from '@Common/Controls';
import Maybe from '@Common/Core/Maybe';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { Asserts } from '@Common/Helpers/Asserts';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import { isPromise } from '@Common/Helpers/CommonUtils';
import { Errors } from '@Common/Helpers/Errors';
import { ISpinnerSetting } from '@Common/Plugins/spinner';
import { AbstractInstanceItem, DocumentBase, InsightObject } from '@InstanceFactory';
import { BaseEditor } from '@Studio/Editors/BaseEditor';

type AnyFunction<T> = (...args: any[]) => T | Promise<T>;

export function withErrorHandling<T>(
    defaultValue: T,
    message?: string
): (target: any, propertyKey: string, descriptor: TypedPropertyDescriptor<AnyFunction<T>>) => void {
    const logError = _.isEmpty(message) ? ex => commonUtils.log(_.toString(ex)) : ex => commonUtils.log(message, _.toString(ex));
    const catchBlock = _.flow(logError, _.constant(defaultValue));

    return (target, propertyKey, descriptor) => {
        const originalFunction = descriptor.value;
        descriptor.value = function (...args: any[]) {
            try {
                const result = originalFunction.apply(this, args);
                if (commonUtils.isPromise(result)) {
                    return result.then(null, catchBlock);
                }

                if (result instanceof Promise) {
                    return result.then(null, catchBlock);
                }
                return result;
            } catch (ex) {
                return catchBlock(ex);
            }
        };
    };
}

export type AllowNullFlagged = { allowNull?: boolean };

export type FunctionWithAllowNullFlagged<TItem> = <TArg extends AllowNullFlagged>(
    target: any,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(arg: TArg) => TItem[]>
) => void;

export function withDefaultItem<TDef>(nullItem: TDef): FunctionWithAllowNullFlagged<TDef> {
    return (target, propertyKey, descriptor) => {
        const originalFunction = descriptor.value;

        descriptor.value = function <T extends AllowNullFlagged>(arg: T) {
            const allowNull = Maybe(arg).get('allowNull').valueOrDefault(false);
            const resultItems = originalFunction.call(this, arg);
            allowNull && resultItems.push(nullItem);
            return resultItems as TDef[];
        };
    };
}

/** @deprecated */
export function withSuppression(...fns: string[]) {
    return withSuppressionTyped<any>(...fns);
}

export function withSuppressionTyped<T>(...fns: (keyof T)[]): (target: T, propertyKey, descriptor) => void;
export function withSuppressionTyped<T>(isPrivate: true, ...fns: string[]): (target: T, propertyKey, descriptor) => void;
export function withSuppressionTyped<T>(...fns: (keyof T | true)[]) {
    return (target: T, propertyKey, descriptor) => {
        const originalFunction = descriptor.value;
        descriptor.value = function (...args: any[]): any {
            const backup = fns
                .filter(f => f !== true)
                .map(f => {
                    const currentFn = this[f];
                    if (MulticastDelegate.isMulticastDelegate(currentFn) && currentFn.enabled) {
                        currentFn.enabled = false;
                        return () => (currentFn.enabled = true);
                    } else {
                        this[f] = _.noop;
                        return () => (this[f] = currentFn);
                    }
                })
                .filter(fn => !!fn);

            const backupFn = () => {
                for (const restore of backup) {
                    restore();
                }
            };

            const result = originalFunction.apply(this, args);

            if (result && 'then' in result && _.isFunction(result.then)) {
                return (result as Promise<any>).then(backupFn, backupFn);
            }
            backupFn();
            return result;
        };
    };
}

export function documentEditor<TDoc extends DocumentBase | InsightObject, T extends BaseEditor<TDoc>>(
    instanceItem: AbstractInstanceItem<TDoc> | string,
    attibutes?: Dictionary<string>
) {
    return (target: GenericConstructor<T>) => {
        if (_.isString(instanceItem)) {
            documentsFactory.registerEditor(instanceItem, attibutes, target);
        } else {
            documentsFactory.registerEditor(instanceItem.instanceName, attibutes, target);
        }
    };
}

type SpinnerContainer =
    | {
          spinnerStart(setting?: ISpinnerSetting): void;
          spinnerStop(): void;
      }
    | { spinnerStart(setting?: ISpinnerSetting): { stop: IHandler } };

type AsyncMethod = Omit<TypedPropertyDescriptor<(...args: any[]) => PromiseLike<any>>, 'get' | 'set'>;

export function asyncUI(setting?: ISpinnerSetting): (target: SpinnerContainer, propertyKey, descriptor: AsyncMethod) => void;
export function asyncUI(
    propertyName?: string,
    setting?: Omit<IAsyncWaiterSetting, 'control'>
): (target: object, propertyKey, descriptor: AsyncMethod) => void;
export function asyncUI(propertyName?: string | ISpinnerSetting, setting?: Omit<IAsyncWaiterSetting, 'control'>) {
    return (target: SpinnerContainer, propertyKey, descriptor: AsyncMethod) => {
        const originalMethod = descriptor.value;

        const spinnerSetting = propertyName === undefined ? {} : Object.isObject(propertyName) ? propertyName : undefined;
        if (spinnerSetting !== undefined) {
            descriptor.value = async function (this: SpinnerContainer, ...args) {
                const spinnerStart = this.spinnerStart(spinnerSetting);
                try {
                    return await originalMethod.apply(this, args);
                } finally {
                    if ('spinnerStop' in this) {
                        this.spinnerStop();
                    } else if (spinnerStart) {
                        spinnerStart.stop();
                    } else {
                        Errors.invalidOperation(`can't stop spinner`);
                    }
                }
            };
        } else {
            descriptor.value = function (...args) {
                const control = this[propertyName as string];
                if (!control) {
                    Errors.invalidOperation('@asyncUI', `Control with name ${propertyName} not found`);
                }
                return asyncWaiter(() => originalMethod.apply(this, args), { ...setting, control }, true);
            };
        }
    };
}

export const lazyInitialize = (defaultValue: any | (() => any)) => {
    return ((t, key: string) => {
        const valueStore = Symbol(key);
        Object.defineProperty(t, key, {
            get: function () {
                return (this[valueStore] = this[valueStore] ?? _.isFunction(defaultValue) ? defaultValue() : defaultValue);
            },
            set: function (value) {
                this[valueStore] = value;
            }
        });
    }) as PropertyDecorator;
};

export const log = (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
    const fn = descriptor.value;
    const counter = new WeakMap();
    descriptor.value = function (...args) {
        const fnCallsCount: Map<string, number> = counter.get(this) ?? counter.set(this, new Map()).get(this);
        const count = fnCallsCount.get(propertyKey) ?? fnCallsCount.set(propertyKey, 1).get(propertyKey);
        fnCallsCount.set(propertyKey, count + 1);
        console.log('before call ', propertyKey, count, ...args);
        const res = fn.apply(this, args);
        if (commonUtils.isPromiseLike(res)) {
            return res.then(
                asyncRes => {
                    console.log('after call ', propertyKey, count, asyncRes, ...args);
                    return asyncRes;
                },
                error => {
                    console.log('after call error ', propertyKey, count, error, ...args);
                    return Promise.reject(error);
                }
            );
        } else {
            console.log('after call ', propertyKey, count, res, ...args);
            return res;
        }
    };
};

/**
 * transform PromiseLike<any> result to JQueryPromise<any>
 */
export function jQueryCompatibility() {
    return <T extends (...args: any[]) => PromiseLike<any>>(target: object, propertyKey: string | symbol, descriptor: TypedPropertyDescriptor<T>) => {
        const fn = descriptor.value;
        Asserts.isNotNull(fn);
        descriptor.value = (function (this: object, ...args: any[]) {
            const res = fn.apply(this, args);
            if (isPromise(res)) {
                return res;
            } else {
                return $.Deferred(deferred => res.then(deferred.resolve, deferred.reject)).promise();
            }
        } as unknown) as T;
    };
}
